import { SelectChangeEvent } from '@mui/material';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  MouseEvent,
} from 'react';
import {
  PRICE_KEY,
  MIN_BATHROOM_KEY,
  MAX_BATHROOM_KEY,
  MAX_BEDROOM_KEY,
  MIN_BEDROOM_KEY,
  SQUAREFEET_KEY,
  STYLE_KEY,
  SPECIAL_FEATURES_KEY,
  FRAME_TYPES_KEY,
  FAMILY_TYPES_KEY,
  MIN_STORIES_KEY,
  MAX_STORIES_KEY,
} from './utils';
import { useGetHomeModelsFilters } from '../../../../api/homes.ts';
import { HomeModelsGenericSlider } from './home-models-generic-slider.tsx';
import { HomeModelsGenericDropdown } from './home-models-generic-dropdown.tsx';
import { HomeModelsGenericSelect } from './home-models-generic-select.tsx';

interface HomeModelsFiltersProps {
  priceRange: number[];
  setPriceRange: Dispatch<SetStateAction<number[]>>;
  squareFeetRange: number[];
  setSquareFeetRange: Dispatch<SetStateAction<number[]>>;
  minBathroom: string;
  setMinBathroom: Dispatch<SetStateAction<string>>;
  maxBathroom: string;
  setMaxBathroom: Dispatch<SetStateAction<string>>;
  minBedroom: string;
  setMinBedroom: Dispatch<SetStateAction<string>>;
  maxBedroom: string;
  setMaxBedroom: Dispatch<SetStateAction<string>>;
  minStories: string;
  setMinStories: Dispatch<SetStateAction<string>>;
  maxStories: string;
  setMaxStories: Dispatch<SetStateAction<string>>;
  styleSelected: string;
  setStyleSelected: Dispatch<SetStateAction<string>>;
  frameTypesSelected: string;
  setFrameTypesSelected: Dispatch<SetStateAction<string>>;
  familyTypesSelected: string;
  setFamilyTypesSelected: Dispatch<SetStateAction<string>>;
  specialFeaturesSelected: string;
  setSpecialFeaturesSelected: Dispatch<SetStateAction<string>>;
}

export const HomeModelsFilters = (props: HomeModelsFiltersProps) => {
  const {
    priceRange,
    setPriceRange,
    squareFeetRange,
    setSquareFeetRange,
    minBathroom,
    setMinBathroom,
    maxBathroom,
    setMaxBathroom,
    minBedroom,
    setMinBedroom,
    maxBedroom,
    setMaxBedroom,
    minStories,
    setMinStories,
    maxStories,
    setMaxStories,
    styleSelected,
    setStyleSelected,
    frameTypesSelected,
    setFrameTypesSelected,
    familyTypesSelected,
    setFamilyTypesSelected,
    specialFeaturesSelected,
    setSpecialFeaturesSelected,
  } = props;

  const { data: HomeModelsFiltersData, loading: loadingFilters } =
    useGetHomeModelsFilters();

  useEffect(() => {
    if (HomeModelsFiltersData) {
      setPriceRange([
        HomeModelsFiltersData?.price?.min || 0,
        HomeModelsFiltersData?.price?.max || 100,
      ]);

      setSquareFeetRange([
        HomeModelsFiltersData?.square_feet?.min || 0,
        HomeModelsFiltersData?.square_feet?.max || 100,
      ]);
    }
  }, [HomeModelsFiltersData, setPriceRange, setSquareFeetRange]);
  const [, setAmountOfOtherFilters] = useState<number>(0);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [bathroomAnchorEl, setBathroomAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const bathroomOpen = Boolean(bathroomAnchorEl);

  const [bedroomAnchorEl, setBedroomAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const bedroomOpen = Boolean(bedroomAnchorEl);

  const [storiesAnchorEl, setStoriesAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const storiesOpen = Boolean(storiesAnchorEl);

  const [squareFeetAnchorEl, setSquareFeetAnchorEl] =
    useState<null | HTMLElement>(null);
  const squareFeetOpen = Boolean(squareFeetAnchorEl);

  const handlePriceClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBathroomClick = (event: MouseEvent<HTMLButtonElement>) => {
    setBathroomAnchorEl(event.currentTarget);
  };

  const handleStoriesClick = (event: MouseEvent<HTMLButtonElement>) => {
    setStoriesAnchorEl(event.currentTarget);
  };

  const handleBedroomClick = (event: MouseEvent<HTMLButtonElement>) => {
    setBedroomAnchorEl(event.currentTarget);
  };

  const handleSquareFeetClick = (event: MouseEvent<HTMLButtonElement>) => {
    setSquareFeetAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setBathroomAnchorEl(null);
    setBedroomAnchorEl(null);
    setSquareFeetAnchorEl(null);
    setStoriesAnchorEl(null);
  };

  const handleChange = (event: SelectChangeEvent, field: string) => {
    switch (field) {
      case STYLE_KEY: {
        setStyleSelected(event.target.value);
        break;
      }
      case FRAME_TYPES_KEY: {
        setFrameTypesSelected(event.target.value);
        break;
      }
      case FAMILY_TYPES_KEY: {
        setFamilyTypesSelected(event.target.value);
        break;
      }
      case SPECIAL_FEATURES_KEY: {
        setSpecialFeaturesSelected(event.target.value);
        break;
      }
    }
  };

  const handleSliderChange = (newValue: number[], field: string) => {
    switch (field) {
      case PRICE_KEY: {
        setPriceRange(newValue);
        break;
      }
      case SQUAREFEET_KEY: {
        setSquareFeetRange(newValue);
        break;
      }
    }
  };

  const handleAutocompleteChange = (newValue: string, field: string) => {
    switch (field) {
      case MAX_BATHROOM_KEY: {
        setMaxBathroom(newValue);
        break;
      }
      case MIN_BATHROOM_KEY: {
        setMinBathroom(newValue);
        break;
      }
      case MAX_BEDROOM_KEY: {
        setMaxBedroom(newValue);
        break;
      }
      case MIN_BEDROOM_KEY: {
        setMinBedroom(newValue);
        break;
      }
      case MIN_STORIES_KEY: {
        setMinStories(newValue);
        break;
      }
      case MAX_STORIES_KEY: {
        setMaxStories(newValue);
        break;
      }
      default: {
        break;
      }
    }
  };

  const formatFilters = (filterLimits: string[]) => {
    return filterLimits.map((item, index) => ({
      label: item,
      value: item,
      id: index + 1,
    }));
  };

  const createRangeFilters = (filterLimits: { min: number; max: number }) => {
    interface Item {
      value: string;
      label: string;
      id: number;
    }
    const rangeFilter: Item[] = [];
    let id = 1;
    for (let i = filterLimits.min; i <= filterLimits.max; i++) {
      rangeFilter.push({ value: `${i}`, label: `${i}`, id });
      id++;
    }
    return rangeFilter;
  };

  useEffect(() => {
    setAmountOfOtherFilters(
      Object.values({
        styleSelected,
        specialFeaturesSelected,
      }).filter((objValue) => !!objValue).length
    );
  }, [styleSelected, specialFeaturesSelected]);

  return (
    <div className="homes-filters">
      <HomeModelsGenericSlider
        title="Price"
        open={open}
        disabled={loadingFilters}
        anchorEl={anchorEl}
        handleChange={handleSliderChange}
        handleClick={handlePriceClick}
        handleClose={handleClose}
        value={priceRange}
        minValue={HomeModelsFiltersData?.price?.min || 0}
        maxValue={HomeModelsFiltersData?.price?.max || 0}
        valueKey={PRICE_KEY}
        money
      />
      <HomeModelsGenericDropdown
        title="Bedrooms"
        open={bedroomOpen}
        disabled={loadingFilters}
        anchorEl={bedroomAnchorEl}
        handleChange={handleAutocompleteChange}
        handleClick={handleBedroomClick}
        handleClose={handleClose}
        minValue={minBedroom}
        minKey={MIN_BEDROOM_KEY}
        maxValue={maxBedroom}
        maxKey={MAX_BEDROOM_KEY}
        options={createRangeFilters(
          HomeModelsFiltersData?.bedrooms || { min: 0, max: 1 }
        )}
      />

      <HomeModelsGenericDropdown
        title="Bathrooms"
        open={bathroomOpen}
        disabled={loadingFilters}
        anchorEl={bathroomAnchorEl}
        handleChange={handleAutocompleteChange}
        handleClick={handleBathroomClick}
        handleClose={handleClose}
        minValue={minBathroom}
        minKey={MIN_BATHROOM_KEY}
        maxValue={maxBathroom}
        maxKey={MAX_BATHROOM_KEY}
        options={createRangeFilters(
          HomeModelsFiltersData?.bathrooms || { min: 0, max: 1 }
        )}
      />

      <HomeModelsGenericSlider
        title="Square Feet"
        open={squareFeetOpen}
        disabled={loadingFilters}
        anchorEl={squareFeetAnchorEl}
        handleChange={handleSliderChange}
        handleClick={handleSquareFeetClick}
        handleClose={handleClose}
        value={squareFeetRange}
        minValue={HomeModelsFiltersData?.square_feet?.min || 0}
        maxValue={HomeModelsFiltersData?.square_feet?.max || 0}
        valueKey={SQUAREFEET_KEY}
      />

      <HomeModelsGenericSelect
        title="Style"
        value={styleSelected}
        disabled={loadingFilters}
        options={formatFilters(
          HomeModelsFiltersData ? HomeModelsFiltersData.styles : ['']
        )}
        keyName={STYLE_KEY}
        handleChange={handleChange}
      />

      <HomeModelsGenericSelect
        title="Frame Types"
        value={frameTypesSelected}
        disabled={loadingFilters}
        options={formatFilters(
          HomeModelsFiltersData ? HomeModelsFiltersData.frame_types : ['']
        )}
        keyName={FRAME_TYPES_KEY}
        handleChange={handleChange}
      />

      <HomeModelsGenericDropdown
        title="Stories"
        open={storiesOpen}
        disabled={loadingFilters}
        anchorEl={storiesAnchorEl}
        handleChange={handleAutocompleteChange}
        handleClick={handleStoriesClick}
        handleClose={handleClose}
        minValue={minStories}
        minKey={MIN_STORIES_KEY}
        maxValue={maxStories}
        maxKey={MAX_STORIES_KEY}
        options={createRangeFilters(
          HomeModelsFiltersData?.stories || { min: 0, max: 1 }
        )}
      />

      <HomeModelsGenericSelect
        title="Family Types"
        value={familyTypesSelected}
        disabled={loadingFilters}
        options={formatFilters(
          HomeModelsFiltersData ? HomeModelsFiltersData.family_types : ['']
        )}
        keyName={FAMILY_TYPES_KEY}
        handleChange={handleChange}
      />
      <HomeModelsGenericSelect
        title=" Features"
        value={specialFeaturesSelected}
        disabled={loadingFilters}
        options={formatFilters(
          HomeModelsFiltersData ? HomeModelsFiltersData.features : ['']
        )}
        keyName={`${SPECIAL_FEATURES_KEY}`}
        handleChange={handleChange}
      />
    </div>
  );
};
